import React from 'react';
import styles from './empty-illustrations.module.css';

interface IllustrationProps {
  className?: string;
}

function IllustrationSvg({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <svg
      viewBox="0 0 100 100"
      className={className ?? styles.svg}
      fill="none"
      stroke="currentColor"
      strokeWidth="1"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {children}
    </svg>
  );
}

export function DocumentsIllustration({ className }: IllustrationProps) {
  return (
    <div className={className ?? styles.wrap}>
      <IllustrationSvg>
        <g opacity="0.6" transform="translate(18, 15) rotate(-6, 22, 30)">
          <rect x="0" y="0" width="40" height="55" rx="2" />
          <path d="M25 0 v15 h15" opacity="0.4" />
          <path d="M10 25 h20" opacity="0.8" />
          <path d="M10 35 h15" opacity="0.6" />
          <path d="M10 45 h10" opacity="0.4" />
        </g>
        <g transform="translate(35, 35) rotate(4, 25, 20)">
          <rect x="0" y="0" width="55" height="40" rx="2" />
          <rect x="4" y="4" width="47" height="32" rx="1" opacity="0.3" />
          <circle cx="38" cy="14" r="5" opacity="0.4" />
          <path d="M4 36 l12 -14 l10 8 l14 -18 l11 14" opacity="0.6" />
        </g>
        <path d="M85 15 l1 -3 l3 -1 l-3 -1 l-1 -3 l-1 3 l-3 1 l3 1 z" opacity="0.4" />
        <path d="M15 85 l0.5 -2 l2 -0.5 l-2 -0.5 l-0.5 -2 l-0.5 2 l-2 0.5 l2 0.5 z" opacity="0.2" />
      </IllustrationSvg>
    </div>
  );
}

/**
 * Empty-state illustration for a single empty DOCUMENT (used in the page card
 * preview when a document has no content). Soft, calm line-art in the house
 * style: a second page peeking behind the front page for gentle depth, with a
 * few fading text lines. Monochrome (`currentColor`), low opacity.
 */
export function DocumentEmptyIllustration({ className }: IllustrationProps) {
  return (
    <div className={className ?? styles.wrap}>
      <IllustrationSvg className={styles.svgSoft}>
        {/* back page (peeking) */}
        <g opacity="0.5">
          <rect x="26" y="24" width="38" height="54" rx="5" />
        </g>
        {/* front page + fading text lines */}
        <g>
          <rect x="38" y="18" width="38" height="54" rx="5" />
          <path d="M45 34 h24" opacity="0.7" />
          <path d="M45 43 h24" opacity="0.5" />
          <path d="M45 52 h14" opacity="0.35" />
        </g>
      </IllustrationSvg>
    </div>
  );
}

/**
 * Empty-state illustration for an empty AUDIO surface. A play button above
 * a waveform — "a recording, ready to listen". Same calm house style as
 * DocumentEmptyIllustration (monochrome `currentColor`, soft 1.5px strokes,
 * low opacity).
 */
export function AudioEmptyIllustration({ className }: IllustrationProps) {
  return (
    <div className={className ?? styles.wrap}>
      <IllustrationSvg className={styles.svgSoft}>
        {/* play button */}
        <circle cx="50" cy="42" r="16" />
        <path d="M46 35 l10 7 l-10 7 z" fill="currentColor" opacity="0.7" />
        {/* waveform */}
        <path d="M22 70 v-3" opacity="0.7" />
        <path d="M30 70 v-9" opacity="0.7" />
        <path d="M38 70 v-5" opacity="0.7" />
        <path d="M46 70 v-11" opacity="0.7" />
        <path d="M54 70 v-6" opacity="0.7" />
        <path d="M62 70 v-10" opacity="0.7" />
        <path d="M70 70 v-4" opacity="0.7" />
        <path d="M78 70 v-7" opacity="0.7" />
      </IllustrationSvg>
    </div>
  );
}

/**
 * Empty-state illustration for an empty CANVAS page card. A pen drawing a curve
 * on a dotted infinite-canvas grid — "start sketching here" — echoing the
 * canvas's freeform/gesture nature. Same calm house style as the document and
 * audio empty states (monochrome `currentColor`, soft 1.5px strokes, low
 * opacity).
 */
export function CanvasEmptyIllustration({ className }: IllustrationProps) {
  return (
    <div className={className ?? styles.wrap}>
      <IllustrationSvg className={styles.svgSoft}>
        {/* dotted infinite-canvas grid */}
        <g fill="currentColor" stroke="none" opacity="0.45">
          <circle cx="26" cy="28" r="1.4" />
          <circle cx="42" cy="28" r="1.4" />
          <circle cx="58" cy="28" r="1.4" />
          <circle cx="74" cy="28" r="1.4" />
          <circle cx="26" cy="44" r="1.4" />
          <circle cx="42" cy="44" r="1.4" />
          <circle cx="58" cy="44" r="1.4" />
          <circle cx="74" cy="44" r="1.4" />
          <circle cx="26" cy="60" r="1.4" />
          <circle cx="42" cy="60" r="1.4" />
          <circle cx="58" cy="60" r="1.4" />
          <circle cx="74" cy="60" r="1.4" />
          <circle cx="26" cy="76" r="1.4" />
          <circle cx="42" cy="76" r="1.4" />
          <circle cx="58" cy="76" r="1.4" />
          <circle cx="74" cy="76" r="1.4" />
        </g>
        {/* a drawn curve + a pen */}
        <path d="M24 66 C 34 46, 50 46, 56 60 S 70 72, 72 50" />
        <g transform="translate(66, 38)">
          <path d="M0 22 l16 -16 l6 6 l-16 16 l-7 1 z" />
          <path d="M12 8 l6 6" opacity="0.5" />
          <path d="M-1 23 l-3 6 l6 -3 z" opacity="0.7" />
        </g>
      </IllustrationSvg>
    </div>
  );
}

export function CollectionsIllustration({ className }: IllustrationProps) {
  return (
    <div className={className ?? styles.wrap}>
      <IllustrationSvg>
        <path d="M30 35 h15 l5 -10 h25 a5 5 0 0 1 5 5 v40 a5 5 0 0 1 -5 5 h-45 a5 5 0 0 1 -5 -5 v-35 z" opacity="0.2" />
        <path d="M25 45 h15 l5 -10 h25 a5 5 0 0 1 5 5 v40 a5 5 0 0 1 -5 5 h-45 a5 5 0 0 1 -5 -5 v-35 z" opacity="0.4" />
        <path d="M20 55 h15 l5 -10 h25 a5 5 0 0 1 5 5 v40 a5 5 0 0 1 -5 5 h-45 a5 5 0 0 1 -5 -5 v-35 z" />
      </IllustrationSvg>
    </div>
  );
}

export function TrashIllustration({ className }: IllustrationProps) {
  return (
    <div className={className ?? styles.wrap}>
      <IllustrationSvg>
        <path d="M30 35 l5 50 a5 5 0 0 0 5 5 h20 a5 5 0 0 0 5 -5 l5 -50" />
        <path d="M25 35 h50" opacity="0.8" />
        <path d="M42 35 v-5 h16 v5" opacity="0.6" />
        <path d="M42 45 l1 30" opacity="0.3" />
        <path d="M50 45 v30" opacity="0.5" />
        <path d="M58 45 l-1 30" opacity="0.3" />
        <circle cx="50" cy="15" r="1" fill="currentColor" opacity="0.3" />
        <circle cx="42" cy="10" r="0.5" fill="currentColor" opacity="0.2" />
        <circle cx="58" cy="20" r="1.5" fill="currentColor" opacity="0.4" />
        <circle cx="65" cy="12" r="0.5" fill="currentColor" opacity="0.1" />
        <circle cx="35" cy="22" r="1" fill="currentColor" opacity="0.2" />
      </IllustrationSvg>
    </div>
  );
}

export function OfflineIllustration({ className }: IllustrationProps) {
  return (
    <div className={className ?? styles.wrap}>
      <IllustrationSvg>
        <g transform="translate(25, 20)">
          <rect x="0" y="0" width="50" height="65" rx="3" fill="var(--surface)" opacity="0.6" />
          <path d="M15 25 h20" opacity="0.4" />
          <path d="M15 35 h20" opacity="0.4" />
          <path d="M15 45 h10" opacity="0.4" />
        </g>
        <g transform="translate(35, 42)">
          <circle cx="15" cy="12" r="20" fill="var(--surface)" stroke="none" />
          <path d="M-2 4 Q15 -8 32 4" opacity="0.3" />
          <path d="M4 12 Q15 4 26 12" opacity="0.6" />
          <circle cx="15" cy="22" r="1.5" fill="currentColor" />
          <line x1="-2" y1="-5" x2="32" y2="28" strokeWidth="1.5" />
        </g>
        <circle cx="20" cy="20" r="1" opacity="0.2" />
        <circle cx="80" cy="70" r="1.5" opacity="0.1" />
        <circle cx="15" cy="80" r="0.5" opacity="0.1" />
      </IllustrationSvg>
    </div>
  );
}
